// ==================== PRICING PAGE ====================

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Check,
  ArrowRight,
  Sparkles,
  HelpCircle,
  MessageSquare,
  Users,
  Layers,
  FileText,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ROUTES } from '../../constants';
import { usePlans } from '../../hooks';
import type { SubscriptionPlan } from '../../types';
import {
  fadeInUp,
  staggerContainer,
  scaleIn,
  hoverLift
} from '../../utils/animations';

// FAQ Data
const faqs = [
  {
    question: 'Can I upgrade or downgrade my plan anytime?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate your billing accordingly.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers for annual plans.'
  },
  {
    question: 'Is there a setup fee?',
    answer: 'The subscription plans have no setup fees. However, if you need assistance with WhatsApp Business API integration setup, we offer a professional setup service for an additional fee. Our team will handle the entire integration process on your behalf.'
  },
  {
    question: 'Can I set up WhatsApp integration myself?',
    answer: 'Absolutely! You can set up WhatsApp Business API integration yourself at no extra cost using our step-by-step documentation. If you prefer hands-off setup, our team can do it for you with our professional setup service.'
  },
  {
    question: 'What happens when I reach my limits?',
    answer: 'You\'ll receive notifications as you approach your limits. You can easily upgrade to a higher plan or purchase add-ons for additional capacity.'
  },
  {
    question: 'Do you offer custom enterprise plans?',
    answer: 'Yes! For large organizations with specific needs, we offer custom enterprise solutions. Contact our sales team to discuss your requirements.'
  },
];

// Format plan limit value
const formatLimit = (value: number): string => {
  if (value === -1) return 'Unlimited';
  if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toString();
};

// Plan names mapping for display
const planDisplayNames: Record<string, string> = {
  STARTER: 'Starter',
  STANDARD: 'Standard',
  PROFESSIONAL: 'Professional',
  ENTERPRISE: 'Enterprise',
};

// Plan descriptions fallback
const planDescriptions: Record<string, string> = {
  STARTER: 'Perfect for small businesses just getting started',
  STANDARD: 'Great for growing teams with more needs',
  PROFESSIONAL: 'For scaling businesses requiring advanced features',
  ENTERPRISE: 'Complete solution for large organizations',
};

const PricingPage = () => {
  const { plans, isLoading, error, refetch } = usePlans();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Calculate yearly savings
  const getYearlySavings = (plan: SubscriptionPlan): number => {
    if (!plan.yearlyPrice) return 0;
    const monthlyCost = Number(plan.monthlyPrice) * 12;
    const yearlyCost = Number(plan.yearlyPrice);
    return Math.round(((monthlyCost - yearlyCost) / monthlyCost) * 100);
  };

  // Get price based on billing cycle
  const getPrice = (plan: SubscriptionPlan): string => {
    const price = billingCycle === 'yearly' && plan.yearlyPrice
      ? Number(plan.yearlyPrice) / 12
      : Number(plan.monthlyPrice);
    return price.toFixed(2);
  };

  // Render plan card
  const renderPlanCard = (plan: SubscriptionPlan) => {
    const savings = getYearlySavings(plan);
    const isPopular = plan.isPopular;

    return (
      <motion.div
        key={plan.id}
        variants={scaleIn}
        whileHover={hoverLift}
        className={`relative ${isPopular ? 'lg:-mt-4 lg:mb-4' : ''}`}
      >
        {isPopular && (
          <div className="absolute -top-[12px] left-1/2 -translate-x-1/2 z-10">
            <Badge variant="orange" className="whitespace-nowrap shadow-lg shadow-(--color-signal-orange)/30">
              <Sparkles size={12} className="mr-1" />
              Most Popular
            </Badge>
          </div>
        )}

        <Card
          className={`h-full p-6 lg:p-8 transition-all duration-300 ${
            isPopular
              ? 'border-2 border-(--color-signal-orange) bg-linear-to-b from-(--color-charcoal-black) to-(--color-midnight-navy)'
              : ''
          }`}
        >
          {/* Plan Header */}
          <div className="mb-6">
            <h3 className="text-xl font-heading font-bold text-white mb-2">
              {planDisplayNames[plan.name] || plan.name}
            </h3>
            <p className="text-sm text-(--color-muted-gray)">
              {plan.description || planDescriptions[plan.name]}
            </p>
          </div>

          {/* Price */}
          <div className="mb-6">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl lg:text-5xl font-heading font-bold text-white">
                ${getPrice(plan)}
              </span>
              <span className="text-(--color-muted-gray)">/month</span>
            </div>
            {billingCycle === 'yearly' && plan.yearlyPrice && (
              <p className="text-sm text-(--color-signal-orange) mt-1">
                Billed ${Number(plan.yearlyPrice).toFixed(2)}/year (Save {savings}%)
              </p>
            )}
          </div>

          {/* Limits */}
          <div className="grid grid-cols-2 gap-3 mb-6 p-4 rounded-xl bg-(--color-midnight-navy)/50">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-(--color-signal-orange)" />
              <div>
                <p className="text-xs text-(--color-muted-gray)">Contacts</p>
                <p className="text-sm font-medium text-white">{formatLimit(plan.maxContacts)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare size={16} className="text-(--color-signal-orange)" />
              <div>
                <p className="text-xs text-(--color-muted-gray)">Messages/mo</p>
                <p className="text-sm font-medium text-white">{formatLimit(plan.maxMessagesPerMonth)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Layers size={16} className="text-(--color-signal-orange)" />
              <div>
                <p className="text-xs text-(--color-muted-gray)">Campaigns</p>
                <p className="text-sm font-medium text-white">{formatLimit(plan.maxCampaigns)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-(--color-signal-orange)" />
              <div>
                <p className="text-xs text-(--color-muted-gray)">Templates</p>
                <p className="text-sm font-medium text-white">{formatLimit(plan.maxTemplates)}</p>
              </div>
            </div>
          </div>

          {/* Features */}
          {plan.features && plan.features.length > 0 && (
            <ul className="space-y-3 mb-8">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={12} className="text-(--color-signal-orange)" />
                  </div>
                  <span className="text-sm text-(--color-soft-gray)">{feature}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTA */}
          <Link to={ROUTES.AUTH.REGISTER} className="block">
            <Button
              variant={isPopular ? 'primary' : 'secondary'}
              fullWidth
              rightIcon={<ArrowRight size={16} />}
            >
              Get Started
            </Button>
          </Link>
        </Card>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
        <motion.div 
          className="absolute top-20 right-1/4 w-[500px] h-[500px] bg-(--color-signal-orange)/8 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Badge variant="orange" className="mb-6">Pricing</Badge>
            </motion.div>
            
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
            >
              Simple, Transparent
              <span className="text-gradient block mt-2">Pricing</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-(--color-soft-gray) leading-relaxed mb-10"
            >
              Choose the plan that fits your business needs. All plans include our core features.
            </motion.p>

            {/* Billing Toggle */}
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-3 p-1.5 rounded-full bg-(--color-charcoal-black) border border-(--color-border-gray)"
            >
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  billingCycle === 'monthly'
                    ? 'bg-(--color-signal-orange) text-white'
                    : 'text-(--color-soft-gray) hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  billingCycle === 'yearly'
                    ? 'bg-(--color-signal-orange) text-white'
                    : 'text-(--color-soft-gray) hover:text-white'
                }`}
              >
                Yearly
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                  Save up to {plans.length > 0 ? getYearlySavings(plans[0]) : 20}%
                </span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-16 lg:py-20">
        <div className="container-custom">
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 size={48} className="text-(--color-signal-orange) animate-spin mb-4" />
              <p className="text-(--color-muted-gray)">Loading plans...</p>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                <AlertCircle size={32} className="text-red-400" />
              </div>
              <p className="text-white font-medium mb-2">Failed to load plans</p>
              <p className="text-(--color-muted-gray) text-sm mb-4">{error}</p>
              <Button 
                variant="secondary" 
                onClick={refetch}
                leftIcon={<RefreshCw size={16} />}
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Plans */}
          {!isLoading && !error && plans.length > 0 && (
            <motion.div
              className={`grid gap-6 lg:gap-8 ${
                plans.length === 1 ? 'max-w-md mx-auto' :
                plans.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' :
                plans.length === 3 ? 'md:grid-cols-2 lg:grid-cols-3' :
                'md:grid-cols-2 lg:grid-cols-4'
              }`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {plans.map((plan) => renderPlanCard(plan))}
            </motion.div>
          )}

          {/* No Plans State */}
          {!isLoading && !error && plans.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-(--color-muted-gray)">No plans available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Features Comparison Note */}
      <section className="py-12 bg-(--color-dark-navy)">
        <div className="container-custom">
          <motion.div
            className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center">
                <Check size={20} className="text-(--color-signal-orange)" />
              </div>
              <span className="text-(--color-soft-gray)">No credit card required to start</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-(--color-border-gray)" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center">
                <Check size={20} className="text-(--color-signal-orange)" />
              </div>
              <span className="text-(--color-soft-gray)">Cancel anytime</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-(--color-border-gray)" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center">
                <Check size={20} className="text-(--color-signal-orange)" />
              </div>
              <span className="text-(--color-soft-gray)">Optional setup assistance available</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">FAQ</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Frequently Asked
              <span className="text-gradient"> Questions</span>
            </h2>
            <p className="text-(--color-muted-gray)">
              Everything you need to know about our pricing and plans.
            </p>
          </motion.div>

          <motion.div
            className="max-w-3xl mx-auto space-y-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="border border-(--color-border-gray) rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left bg-(--color-charcoal-black) hover:bg-(--color-charcoal-black)/80 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  <div className={`w-8 h-8 rounded-full bg-(--color-orange-glow) flex items-center justify-center shrink-0 transition-transform duration-200 ${
                    expandedFaq === index ? 'rotate-45' : ''
                  }`}>
                    <HelpCircle size={16} className="text-(--color-signal-orange)" />
                  </div>
                </button>
                
                <motion.div
                  initial={false}
                  animate={{
                    height: expandedFaq === index ? 'auto' : 0,
                    opacity: expandedFaq === index ? 1 : 0
                  }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-5 pt-0 bg-(--color-charcoal-black)">
                    <p className="text-(--color-muted-gray) leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-r from-(--color-signal-orange)/20 via-(--color-midnight-navy) to-(--color-warm-amber)/20" />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-(--color-signal-orange)/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6"
            >
              Need a Custom
              <span className="text-gradient block mt-2">Enterprise Solution?</span>
            </motion.h2>
            
            <motion.p
              variants={fadeInUp}
              className="text-lg text-(--color-soft-gray) mb-10"
            >
              Get in touch with our sales team to discuss custom pricing, 
              dedicated support, and enterprise features.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to={ROUTES.CONTACT}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="primary" size="lg" rightIcon={<ArrowRight size={18} />}>
                    Contact Sales
                  </Button>
                </motion.div>
              </Link>
              <Link to={ROUTES.AUTH.REGISTER}>
                <Button variant="secondary" size="lg">
                  Start Free
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export { PricingPage };

